/**
 * useComplianceBrief — derives the editorial copy block at the top of the
 * Baseline Compliance page from the active drift / score state.
 *
 * Mirrors `usePostureBrief` exactly: pulls the signed-in user from MSAL,
 * memoises on value-shape (not object identity), and delegates all copy
 * composition to the pure `composeComplianceBrief`. The signature stays
 * stable so the page consumer can extend live-data piping later without
 * touching downstream components.
 */

import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  composeComplianceBrief,
  type ComplianceBrief,
  type Pillar,
} from '@/lib/compliance-brief'

export interface UseComplianceBriefArgs {
  /**
   * Active tenant ID. Combined with the local day to seed editorial variant
   * selection so the brief stays stable for that tenant across the day.
   */
  tenantId?: string
  /** Compliance pillar score (0-100). */
  score?: number | null
  /** 30-day score delta — drives the green pill and the headline modifier. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact priority drift / action — informs the verdict + deck risk slot. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Open critical-severity drift count — drives the weak-link clause. */
  criticalCount?: number
  /**
   * Canonical workload names with active drifts (Identity / Exchange /
   * Teams / SharePoint / Defender). Map M365DSC component prefixes via
   * `getWorkloadForDriftComponent` before passing — the brief expects
   * workload buckets, not resource names or M365DSC resource codes.
   */
  driftingWorkloads?: string[]
}

export function useComplianceBrief(args: UseComplianceBriefArgs = {}): ComplianceBrief {
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Memoise on value-shape rather than object identity so the page can
  // rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionPillar = args.topAction?.pillar
  const topActionTitle = args.topAction?.title
  const topActionImpact = args.topAction?.impact

  // Workload list — stringify for stable memo deps. The list is small
  // (single-digit) so the cost is negligible compared to the full brief
  // recomposition we'd otherwise trigger on every parent re-render.
  const workloadsKey = (args.driftingWorkloads ?? []).join('|')

  return useMemo(
    () =>
      composeComplianceBrief({
        userEmail,
        tenantId: args.tenantId,
        score: args.score,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionPillar && topActionTitle
            ? { pillar: topActionPillar, title: topActionTitle, impact: topActionImpact }
            : undefined,
        criticalCount: args.criticalCount,
        driftingWorkloads: args.driftingWorkloads,
      }),
    [
      userEmail,
      args.tenantId,
      args.score,
      deltaScore,
      deltaDays,
      topActionPillar,
      topActionTitle,
      topActionImpact,
      args.criticalCount,
      args.driftingWorkloads,
      workloadsKey,
    ],
  )
}
